/**
 * Auth Store
 *
 * Zustand store definition for authentication state.
 * Uses persist middleware to save/load state from localStorage.
 *
 * @module @store/auth/authStore
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { checkDevEnv } from '@shared/utils/env';

import type { AuthStore, AuthState } from './authTypes';
import { createAuthActions } from './authActions';

/**
 * Initial state for the auth store.
 */
const initialState: AuthState = {
    isAuthenticated: false,
    accessToken: null,
};

/**
 * Zustand store for authentication state.
 *
 * Uses the persist middleware to automatically save/load state from localStorage.
 * This ensures the auth state persists across page refreshes.
 *
 * DevTools middleware is enabled in development for debugging.
 */
export const useAuthStore = create<AuthStore>()(
    devtools(
        persist(
            (set): AuthStore => ({
                ...initialState,
                ...createAuthActions(set, initialState),
            }),
            {
                name: 'dynee-auth-store',
                // Persist all auth state
                partialize: (state): AuthState => ({
                    isAuthenticated: state.isAuthenticated,
                    accessToken: state.accessToken,
                }),
            },
        ),
        { enabled: checkDevEnv(), name: 'AuthStore' },
    ),
);
